import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DatePicker from 'react-multi-date-picker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import {
    CalendarIcon,
    ChevronDownIcon,
    ExternalLinkIcon,
    EyeIcon,
    FileDownIcon,
    FileSpreadsheetIcon,
    RefreshCwIcon,
    SearchIcon,
    XIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import gregorian_pt from '@/services/gregorian_pt';
import { useFetch } from '@/hooks/useFecth';
import { useSearchTextInput } from '@/hooks/useSearchTextInput';
import server from '@/services/server';
import { useClub } from './ClubContext';
import { dateRangeFromParams, dateRangeLabel, patchSearchParams, serializeDateRange } from './utils/urlListFilters';
import ClubEmptyList from './components/ClubEmptyList';
import SubscriptionDetailsModal from './components/SubscriptionDetailsModal';
import useClubErrors from './hooks/useClubErrors';
import useClubPortalContainer from './hooks/useClubPortalContainer';
import { normalizeSubscriptionStatus, subscriptionStatusLabel } from './utils/subscriptionStatus';

const ALL_VALUE = 'all';

const STATUS_FILTER_ITEMS = {
    [ALL_VALUE]: 'Todas',
    paid: 'Pagas',
    pending: 'Pendentes',
    overdue: 'Atrasadas',
};

const STATUS_BADGE = {
    PENDING: { label: 'Pendente', className: 'border-amber-500/40 text-amber-700 dark:text-amber-400' },
    WAITING: { label: 'Aguardando', className: 'border-amber-500/40 text-amber-700 dark:text-amber-400' },
    AWAITING_RISK_ANALYSIS: {
        label: 'Em análise',
        className: 'border-amber-500/40 text-amber-700 dark:text-amber-400',
    },
    OVERDUE: { label: 'Atrasada', className: 'border-destructive/40 text-destructive' },
    RECEIVED: { label: 'Paga', className: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400' },
    CONFIRMED: { label: 'Confirmada', className: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400' },
    RECEIVED_IN_CASH: {
        label: 'Recebida',
        className: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400',
    },
    REFUNDED: { label: 'Estornada', className: 'text-muted-foreground' },
    REFUND_IN_PROGRESS: { label: 'Estorno em andamento', className: 'text-muted-foreground' },
    CHARGEBACK_REQUESTED: { label: 'Contestação', className: 'border-destructive/40 text-destructive' },
    CHARGEBACK_DISPUTE: { label: 'Contestação em disputa', className: 'border-destructive/40 text-destructive' },
    AWAITING_CHARGEBACK_REVERSAL: {
        label: 'Aguardando reversão',
        className: 'border-amber-500/40 text-amber-700 dark:text-amber-400',
    },
    CANCELED: { label: 'Cancelada', className: 'text-muted-foreground' },
    DELETED: { label: 'Removida', className: 'text-muted-foreground' },
};

const BILLING_LABELS = {
    CREDIT_CARD: 'Cartão de crédito',
    DEBIT_CARD: 'Cartão de débito',
    PIX: 'PIX',
    BOLETO: 'Boleto',
    MONEY: 'Dinheiro',
    UNDEFINED: 'Não informado',
};

function formatCurrency(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDateLabel(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function currentMonthRange() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return [start, end];
}

function StatusBadge({ status }) {
    const meta = STATUS_BADGE[status] || { label: status || '—', className: 'text-muted-foreground' };
    return (
        <Badge variant="outline" className={cn('text-xs', meta.className)}>
            {meta.label}
        </Badge>
    );
}

/** Aviso quando a fatura é de assinatura excluída, cancelada ou encerrada, ou de assinante excluído. */
function subscriptionNotice(invoice) {
    if (invoice.subscription?.deletedAt) return 'Assinatura excluída';
    if (invoice.subscriber?.deletedAt) return 'Assinante excluído';
    const code = normalizeSubscriptionStatus(invoice.subscription?.status);
    if (code === 'CANCELLED') return 'Assinatura cancelada';
    if (code === 'INACTIVE') return 'Assinatura inativa';
    return null;
}

export default function ListClubSales() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { club } = useClub();
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const { search, input, handleChange, sync } = useSearchTextInput(searchParams.get('q') || '');
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsSubscriber, setDetailsSubscriber] = useState(null);
    const [syncingId, setSyncingId] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    const clubPortalContainer = useClubPortalContainer();

    const page = parseInt(searchParams.get('page'), 10) || 1;
    const qFromUrl = searchParams.get('q') || '';
    const status = searchParams.get('status') || '';
    const dueFrom = searchParams.get('dueFrom');
    const dueTo = searchParams.get('dueTo');
    const dueDate = useMemo(() => {
        const fromUrl = dateRangeFromParams(dueFrom, dueTo);
        return fromUrl.length ? fromUrl : currentMonthRange();
    }, [dueFrom, dueTo]);

    useEffect(() => {
        if (qFromUrl !== search) sync(qFromUrl);
    }, [qFromUrl]);

    useEffect(() => {
        if (search === qFromUrl) return;
        patchSearchParams(setSearchParams, { q: search });
    }, [search, qFromUrl, setSearchParams]);

    useEffect(() => {
        if (dueFrom || dueTo) return;
        const [start, end] = serializeDateRange(currentMonthRange());
        patchSearchParams(setSearchParams, { dueFrom: start, dueTo: end }, { resetPage: false });
    }, [dueFrom, dueTo, setSearchParams]);

    const [draftDueDate, setDraftDueDate] = useState(null);

    useEffect(() => {
        setDraftDueDate(null);
    }, [dueFrom, dueTo]);

    const pickerDueDate = draftDueDate ?? dueDate;

    const filters = useMemo(() => {
        const f = { page, limit: 20 };
        if (search) f.search = search;
        if (status) f.status = status;
        const serializedDueDate = serializeDateRange(dueDate);
        if (serializedDueDate.length) f.dueDate = serializedDueDate;
        return f;
    }, [search, status, dueDate, page]);

    const exportFilters = useMemo(() => {
        const params = {};
        if (search) params.search = search;
        if (status) params.status = status;
        if (filters.dueDate) params.dueDate = filters.dueDate;
        return params;
    }, [search, status, filters.dueDate]);

    const { data, isFetching, isLoading, mutate } = useFetch('club/invoices', { params: filters });

    const handleExportSubscriptions = async () => {
        setExportLoading(true);
        try {
            const { data: body } = await server.get('club/sales/export', { params: exportFilters });
            const rows = Array.isArray(body?.data) ? body.data : [];
            const sheetRows = rows.map(row => ({
                Nome: row.Nome ?? '',
                CPF: row.CPF ?? '',
                Plano: row.Plano ?? '',
                SKU: row.SKU ?? '',
                Valor: row.Valor ?? '',
                Status: row.Status ?? '',
            }));
            sheetRows.push({
                Nome: 'Totais',
                CPF: '',
                Plano: '',
                SKU: '',
                Valor: '',
                Status: `${rows.length} assinatura(s)`,
            });
            const ws = XLSX.utils.json_to_sheet(sheetRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Assinaturas');
            const stamp = format(new Date(), 'yyyy-MM-dd-HHmm', { locale: ptBR });
            XLSX.writeFile(wb, `assinaturas-${club?.id ?? 'clube'}-${stamp}.xlsx`);
            enqueueSnackbar(
                rows.length ? `Planilha gerada com ${rows.length} assinatura(s).` : 'Nenhuma assinatura para exportar.',
                { variant: rows.length ? 'success' : 'error' },
            );
        } catch (error) {
            setErrors(error);
            enqueueSnackbar('Erro ao exportar assinaturas.', { variant: 'error' });
        } finally {
            setExportLoading(false);
        }
    };

    const handleExportSubscriptionsPdf = async () => {
        setExportLoading(true);
        try {
            const response = await server.get('club/sales/export', {
                params: { ...exportFilters, format: 'pdf' },
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const stamp = format(new Date(), 'yyyy-MM-dd-HHmm', { locale: ptBR });
            link.href = url;
            link.setAttribute('download', `assinaturas-${club?.id ?? 'clube'}-${stamp}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            enqueueSnackbar('PDF gerado com sucesso.', { variant: 'success' });
        } catch (error) {
            setErrors(error);
            enqueueSnackbar('Erro ao exportar PDF.', { variant: 'error' });
        } finally {
            setExportLoading(false);
        }
    };

    const rows = data?.rows || [];
    const totalPages = data?.pages || data?.totalPages || 1;

    const handleSyncInvoice = async invoice => {
        if (!invoice?.id || !invoice?.chargeId || syncingId) return;
        setSyncingId(invoice.id);
        try {
            await server.post(`club/invoices/${invoice.id}/sync`);
            enqueueSnackbar('Fatura sincronizada com o Asaas.');
            await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setSyncingId(null);
        }
    };

    const PickerTemplate = ({ openCalendar, value, setValue }) => (
        <div className="relative min-w-[240px]">
            <Input
                readOnly
                value={dateRangeLabel(value)}
                placeholder="Vencimento"
                className="cursor-pointer pr-8 pl-9"
                onClick={openCalendar}
            />
            <CalendarIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            {value ? (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="absolute top-1/2 right-1 -translate-y-1/2"
                    onClick={event => {
                        event.stopPropagation();
                        setValue(currentMonthRange());
                    }}
                    aria-label="Resetar para o mês atual"
                >
                    <XIcon className="size-4 text-muted-foreground" />
                </Button>
            ) : null}
        </div>
    );

    const openSubscriberDetails = invoice => {
        if (!invoice?.subscriber?.id) return;
        setDetailsSubscriber({
            id: invoice.subscriber.id,
            name: invoice.subscriber.name,
            document: invoice.subscriber.document,
            email: invoice.subscriber.email,
            phone: invoice.subscriber.phone,
            status: invoice.subscriber.status,
            club,
            subscription: invoice.subscription
                ? {
                      ...invoice.subscription,
                      Plan: invoice.plan,
                  }
                : null,
        });
        setDetailsOpen(true);
    };

    return (
        <div className="flex flex-col gap-6">
            <Card>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
                    <div>
                        <CardTitle>Vendas</CardTitle>
                        <CardDescription>
                            Faturas dos assinantes no período — assinatura, pagamento e dados do cliente.
                        </CardDescription>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            disabled={exportLoading}
                            render={
                                <button
                                    type="button"
                                    className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
                                />
                            }
                        >
                            {exportLoading ? <Spinner className="size-4" /> : <FileDownIcon className="size-4" />}
                            {exportLoading ? 'Exportando…' : 'Exportar'}
                            <ChevronDownIcon className="size-4 opacity-60" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            container={clubPortalContainer}
                            align="end"
                            className="min-w-44"
                        >
                            <DropdownMenuItem disabled={exportLoading} onClick={handleExportSubscriptions}>
                                <FileSpreadsheetIcon />
                                Planilha Excel
                            </DropdownMenuItem>
                            <DropdownMenuItem disabled={exportLoading} onClick={handleExportSubscriptionsPdf}>
                                <FileDownIcon />
                                PDF
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </CardHeader>
            </Card>

            <Card className="overflow-visible">
                <div className="flex flex-wrap items-center gap-2 border-b p-4">
                    <div className="relative min-w-[220px] flex-1">
                        <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Assinante: nome, ID, documento ou e-mail..."
                            value={input}
                            onChange={e => handleChange(e.target.value)}
                            className="pl-8"
                        />
                    </div>

                    <div className="w-[160px]">
                        <Select
                            value={status || ALL_VALUE}
                            onValueChange={value =>
                                patchSearchParams(setSearchParams, { status: value === ALL_VALUE ? '' : value })
                            }
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue>
                                    {STATUS_FILTER_ITEMS[status || ALL_VALUE] || 'Status'}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL_VALUE}>Todas</SelectItem>
                                <SelectItem value="paid">Pagas</SelectItem>
                                <SelectItem value="pending">Pendentes</SelectItem>
                                <SelectItem value="overdue">Atrasadas</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <DatePicker
                        containerClassName="w-auto"
                        onChange={e => {
                            const next = Array.isArray(e) ? e : e ? [e] : [];
                            setDraftDueDate(next);
                            const serialized = serializeDateRange(next);
                            if (serialized.length < 2) return;
                            const [start, end] = serialized;
                            setDraftDueDate(null);
                            patchSearchParams(setSearchParams, { dueFrom: start, dueTo: end });
                        }}
                        format="DD/MM/YYYY"
                        range
                        dateSeparator=" a "
                        locale={gregorian_pt}
                        value={pickerDueDate}
                        render={
                            <PickerTemplate
                                value={pickerDueDate}
                                setValue={() => {
                                    setDraftDueDate(null);
                                    const [start, end] = serializeDateRange(currentMonthRange());
                                    patchSearchParams(setSearchParams, { dueFrom: start, dueTo: end });
                                }}
                            />
                        }
                    />
                </div>

                <CardContent className="p-0">
                    {isFetching || isLoading ? (
                        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                            <Spinner className="size-4" />
                            Carregando faturas...
                        </div>
                    ) : null}

                    {!isFetching && !isLoading && rows.length === 0 ? (
                        <ClubEmptyList
                            title="Nenhuma fatura encontrada"
                            description="Ajuste o período, o status ou a busca pelo assinante."
                        />
                    ) : null}

                    {!isFetching && !isLoading && rows.length > 0 ? (
                        <div className="divide-y">
                            {rows.map(invoice => {
                                const paymentUrl = invoice.invoiceUrl || invoice.bankSlipUrl;
                                const notice = subscriptionNotice(invoice);
                                return (
                                    <div
                                        key={invoice.id}
                                        className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-start sm:justify-between"
                                    >
                                        <div className="min-w-0 flex-1 space-y-2">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="truncate font-medium">
                                                    {invoice.subscriber?.name || 'Assinante não informado'}
                                                </p>
                                                <StatusBadge status={invoice.status} />
                                                {notice ? (
                                                    <Badge variant="outline" className="text-xs text-muted-foreground">
                                                        {notice}
                                                    </Badge>
                                                ) : null}
                                            </div>

                                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                                {invoice.subscriber?.id ? (
                                                    <span>ID {invoice.subscriber.id}</span>
                                                ) : null}
                                                {invoice.subscriber?.document ? (
                                                    <span>{invoice.subscriber.document}</span>
                                                ) : null}
                                                {invoice.subscriber?.email ? (
                                                    <span className="truncate">{invoice.subscriber.email}</span>
                                                ) : null}
                                            </div>

                                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                                <span>
                                                    <span className="text-muted-foreground">Plano: </span>
                                                    {invoice.plan?.name || '—'}
                                                    {invoice.subscription?.billingModel === 'INSTALLMENT'
                                                        ? ` · Anual parcelado${
                                                              invoice.installmentCount ? ` ${invoice.installmentCount}x` : ''
                                                          }`
                                                        : invoice.subscription?.cycle
                                                          ? ` · ${
                                                                String(invoice.subscription.cycle).toUpperCase() === 'YEARLY'
                                                                    ? 'Anual'
                                                                    : 'Mensal'
                                                            }`
                                                          : ''}
                                                    {invoice.installmentNumber
                                                        ? ` · Parcela ${invoice.installmentNumber}${
                                                              invoice.installmentCount ? `/${invoice.installmentCount}` : ''
                                                          }`
                                                        : ''}
                                                </span>
                                                <span>
                                                    <span className="text-muted-foreground">Assinatura: </span>
                                                    #{invoice.subscription?.id || '—'}
                                                    {invoice.subscription?.deletedAt
                                                        ? ' · Excluída'
                                                        : invoice.subscription?.status
                                                          ? ` · ${subscriptionStatusLabel(invoice.subscription.status)}`
                                                          : ''}
                                                </span>
                                                <span>
                                                    <span className="text-muted-foreground">Pagamento: </span>
                                                    {BILLING_LABELS[invoice.billingType] ||
                                                        invoice.billingType ||
                                                        '—'}
                                                </span>
                                            </div>

                                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                                <span>Vencimento {formatDateLabel(invoice.dueDate)}</span>
                                                {invoice.paymentDate ? (
                                                    <span>Pago em {formatDateLabel(invoice.paymentDate)}</span>
                                                ) : null}
                                                <span
                                                    className="font-mono"
                                                    title={
                                                        invoice.chargeId
                                                            ? `ID externo: ${invoice.chargeId}`
                                                            : undefined
                                                    }
                                                >
                                                    #{invoice.id}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end">
                                            <p className="text-base font-semibold tabular-nums">
                                                {formatCurrency(invoice.value)}
                                            </p>
                                            <div className="flex gap-1">
                                                {invoice.chargeId && !invoice.subscription?.deletedAt ? (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon-sm"
                                                        aria-label="Sincronizar com Asaas"
                                                        title="Sincronizar com Asaas"
                                                        disabled={syncingId === invoice.id}
                                                        onClick={() => handleSyncInvoice(invoice)}
                                                    >
                                                        {syncingId === invoice.id ? (
                                                            <Spinner className="size-4" />
                                                        ) : (
                                                            <RefreshCwIcon className="size-4" />
                                                        )}
                                                    </Button>
                                                ) : null}
                                                {invoice.subscriber?.id ? (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon-sm"
                                                        aria-label="Ver assinante"
                                                        onClick={() => openSubscriberDetails(invoice)}
                                                    >
                                                        <EyeIcon className="size-4" />
                                                    </Button>
                                                ) : null}
                                                {paymentUrl ? (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon-sm"
                                                        aria-label="Abrir fatura"
                                                        title="Abrir fatura"
                                                        onClick={() =>
                                                            window.open(paymentUrl, '_blank', 'noopener')
                                                        }
                                                    >
                                                        <ExternalLinkIcon className="size-4" />
                                                    </Button>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}

                    {totalPages > 1 ? (
                        <div className="flex items-center justify-center gap-3 border-t px-4 py-4">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() =>
                                    setSearchParams(prev => {
                                        const params = new URLSearchParams(prev);
                                        params.set('page', String(page - 1));
                                        return params;
                                    })
                                }
                            >
                                Anterior
                            </Button>
                            <span className="text-sm text-muted-foreground">
                                Página {page} de {totalPages}
                            </span>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={page >= totalPages}
                                onClick={() =>
                                    setSearchParams(prev => {
                                        const params = new URLSearchParams(prev);
                                        params.set('page', String(page + 1));
                                        return params;
                                    })
                                }
                            >
                                Próxima
                            </Button>
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            <SubscriptionDetailsModal
                club={club}
                open={detailsOpen}
                onClose={() => {
                    setDetailsOpen(false);
                    setDetailsSubscriber(null);
                }}
                subscriber={detailsSubscriber}
                onChanged={mutate}
            />
        </div>
    );
}
